import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import PageHeader from '../../components/ui/PageHeader';
import DateRangeField from '../../components/ui/DateRangeField';

const MONTH_NAMES = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function fmtMonth(ym) {
  const [y, m] = String(ym).split('-').map((n) => Number(n));
  if (!y || !m) return ym;
  return `${MONTH_NAMES[m - 1] || m} ${y}`;
}

function fmtMoney(v) {
  const n = Number(v || 0);
  if (!n) return '—';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

function fmtPct(v) {
  const n = Number(v || 0);
  return `${n.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function defaultDates() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 5);
  from.setDate(1);
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: ymd(from), to: ymd(to) };
}

export default function AnalyticsPnl() {
  const theme = useTheme();
  const { error: showError } = useNotify();
  const [{ from, to }, setRange] = useState(defaultDates);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get('/analytics/finance/pnl', {
        params: { date_from: from, date_to: to },
      });
      setData(resp);
    } catch (e) {
      showError?.(e?.response?.data?.message || 'Не удалось загрузить P&L');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const months = data?.months || [];

  const toggle = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const colors = useMemo(() => ({
    income: theme.palette.success.main,
    expense: theme.palette.error.main,
    calc: theme.palette.text.primary,
    percent: theme.palette.info.main,
    info: theme.palette.text.secondary,
  }), [theme]);

  // плоский список видимых строк с учётом раскрытых узлов
  const visibleRows = useMemo(() => {
    if (!data?.rows) return [];
    const out = [];
    const walk = (nodes, depth) => {
      for (const node of nodes) {
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        const isOpen = expanded.has(node.key);
        out.push({ node, depth, hasChildren, isOpen });
        if (hasChildren && isOpen) walk(node.children, depth + 1);
      }
    };
    walk(data.rows, 0);
    return out;
  }, [data, expanded]);

  const rowTotal = useCallback((row) => {
    if (row.type === 'percent') return null;
    return months.reduce((acc, ym) => acc + Number(row.values?.[ym] || 0), 0);
  }, [months]);

  const stickyCellSx = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    bgcolor: theme.palette.background.paper,
    minWidth: 280,
    maxWidth: 360,
  };

  const colCount = months.length + 2;

  const renderRow = ({ node, depth, hasChildren, isOpen }) => {
    const total = rowTotal(node);
    const baseColor = colors[node.type] || theme.palette.text.primary;
    const isTop = depth === 0;
    // справочная строка (внутренние перемещения) — не участвует в расчётах
    const isInfo = node.type === 'info';
    // подсветка фоновой заливкой для верхнего уровня чистой прибыли
    const rowBg = isInfo
      ? alpha(theme.palette.text.primary, 0.03)
      : (isTop && (node.key === 'net_profit' || node.key === 'net_margin_pct')
        ? alpha(theme.palette.success.main, 0.10)
        : (isTop && node.key === 'opex' ? alpha(theme.palette.warning.main, 0.06) : undefined));
    const labelWeight = isInfo ? 400 : (isTop ? 700 : (depth === 1 ? 600 : 400));
    const valueWeight = isInfo ? 400 : (isTop ? 700 : (depth === 1 ? 500 : 400));

    return (
      <TableRow
        key={node.key}
        hover
        sx={{
          ...(rowBg ? { bgcolor: rowBg } : {}),
          ...(isTop ? { '& td': { borderTop: `1px solid ${theme.palette.divider}` } } : {}),
        }}
      >
        <TableCell
          sx={{
            ...stickyCellSx,
            pl: 1 + depth * 2.5,
            ...(rowBg ? { bgcolor: rowBg } : {}),
          }}
          onClick={hasChildren ? () => toggle(node.key) : undefined}
          style={hasChildren ? { cursor: 'pointer' } : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggle(node.key); }}
                sx={{ p: 0.25 }}
              >
                {isOpen ? <ArrowDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: labelWeight,
                color: (isInfo || depth > 1) ? 'text.secondary' : baseColor,
                fontStyle: isInfo ? 'italic' : 'normal',
              }}
            >
              {node.label}
            </Typography>
          </Box>
        </TableCell>

        {months.map((ym) => {
          const v = node.values?.[ym];
          const text = node.type === 'percent' ? fmtPct(v) : fmtMoney(v);
          const isNeg = Number(v || 0) < 0;
          return (
            <TableCell
              key={ym}
              align="right"
              sx={{
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: valueWeight,
                fontStyle: isInfo ? 'italic' : 'normal',
                color: isInfo ? theme.palette.text.secondary : (isNeg ? theme.palette.error.main : baseColor),
                ...(rowBg ? { bgcolor: rowBg } : {}),
              }}
            >
              {text}
            </TableCell>
          );
        })}

        <TableCell
          align="right"
          sx={{
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: isInfo ? 400 : 700,
            fontStyle: isInfo ? 'italic' : 'normal',
            color: isInfo
              ? theme.palette.text.secondary
              : (total != null && total < 0 ? theme.palette.error.main : baseColor),
            borderLeft: `1px solid ${theme.palette.divider}`,
            ...(rowBg ? { bgcolor: rowBg } : {}),
          }}
        >
          {total == null ? '—' : fmtMoney(total)}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <PageHeader
        title="P&L — прибыли и убытки"
        subtitle="Помесячный отчёт: итоги по всем организациям и маркетплейсам. Кликните по строке, чтобы раскрыть детализацию (организации → маркетплейсы / объекты расходов)"
        actions={(
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ minWidth: 260 }}>
              <DateRangeField
                valueFrom={from}
                valueTo={to}
                onChange={({ from: f, to: t }) => setRange({ from: f, to: t })}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              sx={{ borderRadius: 9999, textTransform: 'none' }}
            >
              Обновить
            </Button>
          </Stack>
        )}
      />

      {data?.meta && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip
            size="small"
            label={`Себестоимость WB: ${data.meta.cost_available?.wb ? 'есть' : 'нет данных'}`}
            color={data.meta.cost_available?.wb ? 'success' : 'default'}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Ozon: ${data.meta.cost_available?.ozon ? 'есть' : 'нет данных'}`}
            color={data.meta.cost_available?.ozon ? 'success' : 'default'}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Yandex: ${data.meta.cost_available?.yandex ? 'есть' : 'нет данных'}`}
            color={data.meta.cost_available?.yandex ? 'success' : 'default'}
            variant="outlined"
          />
          <Chip size="small" label={`УСН ${Math.round((data.meta.usn_rate || 0) * 100)}%`} variant="outlined" />
          {typeof data.meta.org_count === 'number' && (
            <Chip size="small" label={`Организаций: ${data.meta.org_count}`} variant="outlined" />
          )}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !data || !months.length || !(data.rows || []).length ? (
          <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Нет данных за выбранный период</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...stickyCellSx,
                      zIndex: 3,
                      bgcolor: theme.palette.surfaceContainerHigh || theme.palette.background.paper,
                      fontWeight: 700,
                    }}
                  >
                    Показатель
                  </TableCell>
                  {months.map((ym) => (
                    <TableCell key={ym} align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                      {fmtMonth(ym)}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{ whiteSpace: 'nowrap', fontWeight: 700, borderLeft: `1px solid ${theme.palette.divider}` }}
                  >
                    Итого
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      Нет данных
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((vr) => renderRow(vr))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );
}
